'use client';

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

interface CartItem {
  id: string;
  name: string;
  price: number;
  quantity: number;
  image?: string;
}

const ORDER_CITY_KEY = 'foodie-express-order-city';

interface CartContextType {
  cartItems: CartItem[];
  /** City selected on menu catalog; sent with order for Sales Analytics */
  orderCityId: string;
  setOrderCityId: (cityId: string) => void;
  addToCart: (item: CartItem) => void;
  updateQuantity: (itemId: string, quantity: number) => void;
  removeFromCart: (itemId: string) => void;
  clearCart: () => void;
  getTotalItems: () => number;
  showToast: (message: string, type?: 'success' | 'error' | 'info') => void;
}

interface ToastMessage {
  id: string;
  message: string;
  type: 'success' | 'error' | 'info';
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export const CartProvider = ({ children }: { children: ReactNode }) => {
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [orderCityId, setOrderCityIdState] = useState<string>('');
  const [toasts, setToasts] = useState<ToastMessage[]>([]);
  const [isHydrated, setIsHydrated] = useState(false);

  const setOrderCityId = (cityId: string) => {
    setOrderCityIdState(cityId);
    if (typeof localStorage !== 'undefined') localStorage.setItem(ORDER_CITY_KEY, cityId);
  };

  // Load cart and order city from localStorage on mount
  useEffect(() => {
    const savedCart = localStorage.getItem('foodie-express-cart');
    if (savedCart) {
      try {
        setCartItems(JSON.parse(savedCart));
      } catch (error) {
        console.error('Failed to parse cart from localStorage:', error);
      }
    }
    const savedCity = localStorage.getItem(ORDER_CITY_KEY);
    if (savedCity) setOrderCityIdState(savedCity);
    setIsHydrated(true);
  }, []);

  // Save cart to localStorage whenever it changes
  useEffect(() => {
    if (isHydrated) {
      localStorage.setItem('foodie-express-cart', JSON.stringify(cartItems));
    }
  }, [cartItems, isHydrated]);

  const showToast = (message: string, type: 'success' | 'error' | 'info' = 'success') => {
    const id =
      typeof crypto !== 'undefined' && 'randomUUID' in crypto
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    setToasts((prev) => [...prev, { id, message, type }]);
    setTimeout(() => {
      setToasts((prev) => prev.filter((toast) => toast.id !== id));
    }, 3000);
  };

  const addToCart = (newItem: CartItem) => {
    setCartItems((prev) => {
      const existingItem = prev.find((item) => item.id === newItem.id);
      if (existingItem) {
        showToast(`Updated ${newItem.name} quantity in cart`, 'success');
        return prev.map((item) =>
          item.id === newItem.id ? { ...item, quantity: item.quantity + newItem.quantity } : item
        );
      }
      showToast(`${newItem.name} added to cart!`, 'success');
      return [...prev, newItem];
    });
  };

  const updateQuantity = (itemId: string, quantity: number) => {
    if (quantity < 1) return;
    setCartItems((prev) => prev.map((item) => (item.id === itemId ? { ...item, quantity } : item)));
  };

  const removeFromCart = (itemId: string) => {
    const item = cartItems.find((i) => i.id === itemId);
    if (item) {
      showToast(`${item.name} removed from cart`, 'info');
    }
    setCartItems((prev) => prev.filter((item) => item.id !== itemId));
  };

  const clearCart = () => {
    setCartItems([]);
    showToast('Cart cleared', 'info');
  };

  const clearOrderCityId = () => {
    setOrderCityIdState('');
    if (typeof localStorage !== 'undefined') localStorage.removeItem(ORDER_CITY_KEY);
  };

  const getTotalItems = () => {
    return cartItems.reduce((sum, item) => sum + item.quantity, 0);
  };

  return (
    <CartContext.Provider
      value={{
        cartItems,
        orderCityId: orderCityId || '',
        setOrderCityId,
        addToCart,
        updateQuantity,
        removeFromCart,
        clearCart,
        getTotalItems,
        showToast,
      }}
    >
      {children}
      {/* Toast Container */}
      <div className="fixed top-24 right-6 z-500 space-y-2">
        {toasts.map((toast) => (
          <div
            key={toast.id}
            className={`animate-slide-in-right rounded-lg px-6 py-3 shadow-lg transition-smooth ${
              toast.type === 'success'
                ? 'bg-success text-white'
                : toast.type === 'error'
                  ? 'bg-error text-white'
                  : 'bg-primary text-primary-foreground'
            }`}
            role="alert"
          >
            <p className="text-sm font-medium">{toast.message}</p>
          </div>
        ))}
      </div>
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};
